import { useI18n } from '../../../../../hooks/useI18n';
import { useNotificationStore } from '../../../../../hooks/useNotificationStore/notificationStore';
import React, { useEffect, useState } from 'react';
import { ApiError, useApiRequests } from '../../../../../hooks/useApi';
import { messages } from '../../../../../messages';
import { useFormik } from 'formik';
import { ColorResult, TwitterPicker } from 'react-color';
import { Input, Modal } from 'antd';
import { LoadingIndicator } from '../../../../loadingIndicator/loadingIndicator';
import { ApiErrorDetails } from '../../../../apiErrorDetails/apiErrorDetails';
import { Form } from '../../../../form/form';
import style from '../styling/widgetRowStyling.module.scss';
import { defaultWidgetColors } from '../../../../internal/widgets/widgetColors';
import {
  customerAwaitingDeliveriesWidgetApi,
  CreateUpdateAwaitingDeliveriesWidgetRequest,
} from '../../../../../api/widgets/customers/customerAwaitingDeliveriesWidgetApi';
import { CustomerWidgetCategorySelect } from '../categories/customerWidgetCategorySelect';
import { CustomerBranchPicker } from '../../../../pickers/customerBranchPicker';

export function UpdateCustomerAwaitingDeliveriesWidgetModal({
  userWidgetId,
  onCancel,
  onSubmit,
}: {
  userWidgetId: number;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  const { t } = useI18n();
  const { notify } = useNotificationStore();

  const [error, setError] = useState<ApiError>();

  const [name, setName] = useState<string>('');
  const [widgetCategoryId, setWidgetCategoryId] = useState<number | undefined>(
    undefined,
  );
  const [color, setColor] = useState<string | undefined>(undefined);
  const [branchIds, setBranchIds] = useState<number[]>([]);

  const updateAwaitingDeliveriesWidget = useApiRequests(
    customerAwaitingDeliveriesWidgetApi.updateWidget,
  );

  const widget = useApiRequests(customerAwaitingDeliveriesWidgetApi.getWidget);

  const handleFormSubmit = () => {
    const updateWidgetData: CreateUpdateAwaitingDeliveriesWidgetRequest = {
      name: name,
      widgetCategoryId: widgetCategoryId,
      color: color,
      branchIds: branchIds,
    };

    updateAwaitingDeliveriesWidget
      .create({
        onSuccess: () => {
          notify({
            title: t(messages.widgets.widgetUpdated),
            type: 'success',
          });
          onSubmit();
        },
        onError: setError,
      })
      .send(userWidgetId, updateWidgetData);
  };

  useEffect(() => {
    widget
      .create({
        onSuccess: (response) => {
          setName(response.name);
          setWidgetCategoryId(response.widgetCategoryId);
          setColor(response.color);
          setBranchIds(response.branches.map((w) => w.id));
        },
        onError: setError,
      })
      .send(userWidgetId);
  }, []);

  const formik = useFormik<FormData>({
    initialValues: {
      name: '',
      widgetCategoryId: undefined,
      color: undefined,
      branchIds: [],
    },
    onSubmit: handleFormSubmit,
  });

  function handleWidgetCategoryOnChange(widgetCategoryId: string | undefined) {
    const id =
      widgetCategoryId === undefined
        ? widgetCategoryId
        : parseInt(widgetCategoryId);
    setWidgetCategoryId(id);
  }

  function handleSelectedBranches(selectedBranchIds: number[]) {
    setBranchIds(selectedBranchIds);
  }

  function handleChangeColor(color: ColorResult) {
    setColor(color.hex);
  }

  return (
    <Modal
      title={t(messages.widgets.awaitingDeliveries.update)}
      visible={true}
      closable={false}
      onCancel={onCancel}
      onOk={formik.submitForm}
      okText={t(messages.shared.save)}
      width={720}
    >
      {updateAwaitingDeliveriesWidget.isLoading && <LoadingIndicator />}
      {error && <ApiErrorDetails error={error} />}
      <Form form={formik}>
        <div className={style.inputRow}>
          <h4>{t(messages.shared.name)}</h4>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t(messages.widgets.widgetNamePlaceholder)}
          />
          <CustomerWidgetCategorySelect
            value={widgetCategoryId?.toString()}
            onChange={handleWidgetCategoryOnChange}
          />
        </div>
        <div className={style.inputRow}>
          <h4>{t(messages.shared.color)}</h4>
          <TwitterPicker
            width="100%"
            triangle="hide"
            colors={defaultWidgetColors}
            color={color}
            onChange={handleChangeColor}
          />
        </div>
        <div className={style.inputRow}>
          <h4>{t(messages.shared.branch)}</h4>
          <CustomerBranchPicker
            branchIds={branchIds}
            onChange={handleSelectedBranches}
          />
        </div>
      </Form>
    </Modal>
  );
}

interface FormData {
  name: string;
  widgetCategoryId: number | undefined;
  color: string | undefined;
  branchIds: number[];
}
